/* eslint-disable dot-notation */
import { registerBrick, BrickContext } from 'olympe';
import { ReactBrick, useProperty, getLogger } from '@olympeio/core';
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { getFilterDrawer } from '../shared/FilterDrawer.jsx';
import { MasterDataAutoComplete } from '../shared/MasterDataAutoComplete.jsx';

class MassChangeFilterDrawer extends ReactBrick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @returns {function(object): React.ReactElement}
     */
    static getReactComponent($) {
        const getOptions = $.get('get filter options');
        const positionOptions = $.get('get options');

        return () => {
            const logger = getLogger('BlockedFilterDrawer');
            // Variables declaration

            const [agents, setAgents] = useState([]);
            const [approvers, setApprovers] = useState([]);
            const [costCenters, setCostCenters] = useState([]);
            const [personRespId, setPersonRespId] = useState([]);
            const [internalWBS, setInternalWBS] = useState([]);
            const [wbs, setWbs] = useState([]);
            const [companyCodes, setCompanyCodes] = useState([]);
            const [plants, setPlants] = useState([]);
            const [purchasingOrgs, setPurchasingOrgs] = useState([]);

            const [purchasingGroup, setPurchasingGroup] = useState([]);
            const [releaseGroups, setReleaseGroups] = useState([]);
            const [releaseCodes, setReleaseCodes] = useState([]);

            const [numberOfHits, setNumberOfHits] = useState('');

            const [formInvalid, setFormInvalid] = useState(false);

            // listen to properties
            const operation = useProperty($, 'Operation');
            const open = useProperty($, 'open');
            const data = useProperty($, 'filterValues') || {};

            const applicableFilters =
                operation === 'PO'
                    ? ['agents', 'purchasingGroup', 'purchasingOrgs', 'releaseGroups', 'releaseCodes', 'numberOfHits']
                    : [
                          'agents',
                          'costCenters',
                          'personRespId',
                          'internalWBS',
                          'wbs',
                          'companyCodes',
                          'plants',
                          'purchasingOrgs',
                          'releaseGroups',
                          'releaseCodes',
                          'numberOfHits',
                      ];

            React.useEffect(() => {
                setFormInvalid(
                    agents.length === 0 &&
                        costCenters.length === 0 &&
                        personRespId.length === 0 &&
                        internalWBS.length === 0 &&
                        companyCodes.length === 0 &&
                        wbs.length === 0 &&
                        plants.length === 0 &&
                        purchasingOrgs.length === 0 &&
                        purchasingGroup.length === 0 &&
                        releaseGroups.length === 0 &&
                        releaseCodes.length === 0 &&
                        approvers?.length === 0
                );
            }, [
                agents,
                costCenters,
                personRespId,
                internalWBS,
                wbs,
                companyCodes,
                plants,
                purchasingOrgs,
                purchasingGroup,
                releaseGroups,
                releaseCodes,
                approvers,
            ]);

            // Close popup method
            const toggleDrawer = (open) => {
                return (event) => {
                    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
                        return;
                    }

                    $.set('open', open);
                };
            };

            // Forward data to draw & close the popup when applying
            const onApply = (event) => {
                const filters = {
                    agents: applicableFilters.includes('agents') ? agents : undefined,
                    costCenters: applicableFilters.includes('costCenters') ? costCenters : undefined,
                    personRespId: applicableFilters.includes('personRespId') ? personRespId : undefined,
                    internalWBS: applicableFilters.includes('internalWBS') ? internalWBS : undefined,
                    wbs: applicableFilters.includes('wbs') ? wbs : undefined,
                    companyCodes: applicableFilters.includes('companyCodes') ? companyCodes : undefined,
                    plants: applicableFilters.includes('plants') ? plants : undefined,
                    purchasingOrgs: applicableFilters.includes('purchasingOrgs') ? purchasingOrgs : undefined,
                    purchasingGroup: applicableFilters.includes('purchasingGroup') ? purchasingGroup : undefined,
                    releaseGroups: applicableFilters.includes('releaseGroups') ? releaseGroups : undefined,
                    releaseCodes: applicableFilters.includes('releaseCodes') ? releaseCodes : undefined,
                    numberOfHits: applicableFilters.includes('numberOfHits') ? numberOfHits : undefined,
                    approvers: applicableFilters.includes('agents') ? approvers : undefined,
                };

                console.log('setting selectedFilters', filters);

                $.set('selectedFilters', filters);

                toggleDrawer(false)(event);
            };

            // Display the form
            const getFilterList = () => (
                <Box role="presentation" onClose={toggleDrawer(false)} sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        {applicableFilters.includes('numberOfHits') && (
                            <Grid item xs={12}>
                                <TextField
                                    id="number-of-hits"
                                    label="Number of Hits"
                                    type="number"
                                    value={numberOfHits || ''}
                                    onChange={(e) => {
                                        if (e.target.value >= 0) {
                                            setNumberOfHits(e.target.value);
                                        }
                                    }}
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <hr style={{ borderTop: '2px dashed #CCC' }} />
                            <Typography variant="caption">Visibility Report Filters (only for display)</Typography>
                        </Grid>

                        {applicableFilters.includes('purchasingOrgs') && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    id="purchasing-orgs"
                                    options={data.PurchaseOrg || []}
                                    getOptionLabel={(option) => option?.Ekorg + ' - ' + option?.Ekotx}
                                    renderInput={(params) => <TextField {...params} label="Purchasing Organizations" />}
                                    onChange={(e, v) => setPurchasingOrgs(v)}
                                    value={purchasingOrgs || []}
                                    fullWidth
                                    multiple
                                />
                            </Grid>
                        )}

                        {applicableFilters.includes('companyCodes') && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    id="company-code"
                                    options={data.CompanyCode || []}
                                    getOptionLabel={(option) => option?.CompCode + ' - ' + option?.CompCodeDesc}
                                    renderInput={(params) => <TextField {...params} label="Company Code" />}
                                    onChange={(e, v) => setCompanyCodes(v)}
                                    value={companyCodes || []}
                                    fullWidth
                                    multiple
                                />
                            </Grid>
                        )}

                        {applicableFilters.includes('plants') && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    id="plants"
                                    options={data.Plants || []}
                                    getOptionLabel={(option) => option?.PlantCode + ' - ' + option?.PlantName}
                                    renderInput={(params) => <TextField {...params} label="Plant" />}
                                    onChange={(e, v) => setPlants(v)}
                                    value={plants || []}
                                    fullWidth
                                    multiple
                                />
                            </Grid>
                        )}

                        {applicableFilters.includes('releaseGroups') && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    id="release-group"
                                    options={data.ReleaseGroup || []}
                                    getOptionLabel={(option) => option?.Frggr + ' - ' + option?.Frggt}
                                    renderInput={(params) => <TextField {...params} label="Release Group" />}
                                    onChange={(e, v) => setReleaseGroups(v)}
                                    value={releaseGroups || []}
                                    fullWidth
                                    multiple
                                />
                            </Grid>
                        )}

                        {applicableFilters.includes('releaseCodes') && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    id="release-code"
                                    options={
                                        data.ReleaseCode?.filter((rc) =>
                                            releaseGroups.length === 0
                                                ? true
                                                : releaseGroups?.find((rg) => rg.Frggr === rc.Frggr) !== undefined
                                        ) || []
                                    }
                                    getOptionLabel={(option) =>
                                        option?.Frgco + ' - ' + option?.Frgct + ' (' + option?.Frggr + ')'
                                    }
                                    renderInput={(params) => <TextField {...params} label="Release Code" />}
                                    onChange={(e, v) => setReleaseCodes(v)}
                                    value={releaseCodes || []}
                                    fullWidth
                                    multiple
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <hr style={{ borderTop: '2px dashed #CCC' }} />
                            <Typography variant="caption">Mass Change Filters (for update)</Typography>
                        </Grid>

                        {applicableFilters.includes('purchasingGroup') && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    id="purchasing-groups"
                                    options={data.PurchaseGroup || []}
                                    getOptionLabel={(option) => option?.PurchGroup + ' - ' + option?.PurchGroupName}
                                    renderInput={(params) => <TextField {...params} label="Purchasing Group" />}
                                    onChange={(e, v) => setPurchasingGroup(v)}
                                    value={purchasingGroup || []}
                                    fullWidth
                                    multiple
                                />
                            </Grid>
                        )}

                        {applicableFilters.includes('agents') && (
                            <>
                                <Grid item xs={12}>
                                    <MasterDataAutoComplete
                                        context={$}
                                        logger={logger}
                                        getOptions={getOptions}
                                        path={'ApproverSet'}
                                        filter={(value) =>
                                            `${operation === 'PO' ? `IdNo eq 'PO' and` : ''} SearchTerm eq '${value}'`
                                        }
                                        value={agents ?? []}
                                        getOptionLabel={(option) =>
                                            option ? `${option.ApproverId} - ${option.ApproverName}` : ''
                                        }
                                        isOptionEqualToValue={(option, value) => option.ApproverId === value.ApproverId}
                                        onChange={(value) => setAgents(value)}
                                        textFieldLabel="Approvers"
                                        multiple={true}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <MasterDataAutoComplete
                                        context={$}
                                        logger={logger}
                                        getOptions={positionOptions}
                                        path={'PositionIdSet'}
                                        filter={(value) => `IdNo eq '01' and SearchTerm eq '${value}'`}
                                        value={approvers ?? []}
                                        getOptionLabel={(option) =>
                                            option ? `${option.PosId} - ${option.PosIdName}` : ''
                                        }
                                        isOptionEqualToValue={(option, value) => option.PosId === value.PosId}
                                        onChange={(value) => setApprovers(value)}
                                        textFieldLabel="Position"
                                        multiple={true}
                                    />
                                </Grid>
                            </>
                        )}

                        {applicableFilters.includes('costCenters') && (
                            <Grid item xs={12}>
                                <MasterDataAutoComplete
                                    context={$}
                                    logger={logger}
                                    getOptions={getOptions}
                                    path={'CostCenterSet'}
                                    filter={(value) => `IdNo eq '02' and (SearchTerm eq '${value}')`}
                                    value={costCenters ?? []}
                                    getOptionLabel={(option) =>
                                        option ? `${option.CostCenterId} - ${option.CostCenterDescription}` : ''
                                    }
                                    isOptionEqualToValue={(option, value) => option.CostCenterId === value.CostCenterId}
                                    onChange={(value) => setCostCenters(value)}
                                    textFieldLabel="Cost Centers"
                                    multiple={true}
                                />
                            </Grid>
                        )}

                        {applicableFilters.includes('personRespId') && (
                            <Grid item xs={12}>
                                <MasterDataAutoComplete
                                    context={$}
                                    logger={logger}
                                    getOptions={getOptions}
                                    path={'WbsPrnSet'}
                                    filter={(value) => `IdNo eq '02' and (PrnNo eq '${value}')`}
                                    value={personRespId ?? []}
                                    getOptionLabel={(option) => (option ? `${option.PrnNo} - ${option.PrnName}` : '')}
                                    isOptionEqualToValue={(option, value) => option.PrnNo === value.PrnName}
                                    onChange={(value) => setPersonRespId(value)}
                                    textFieldLabel="Person Responsible ID (PRN)"
                                    multiple={true}
                                />
                            </Grid>
                        )}

                        {applicableFilters.includes('internalWBS') && (
                            <Grid item xs={12}>
                                <MasterDataAutoComplete
                                    context={$}
                                    logger={logger}
                                    getOptions={getOptions}
                                    path={'WbsPrnSet'}
                                    filter={(value) => `IdNo eq '06' and (PrnNo eq '${value}')`}
                                    value={internalWBS ?? []}
                                    getOptionLabel={(option) => (option ? `${option.PrnNo} - ${option.PrnName}` : '')}
                                    isOptionEqualToValue={(option, value) => option.PrnNo === value.PrnName}
                                    onChange={(value) => setInternalWBS(value)}
                                    textFieldLabel="Internal WBS (for specific markets in Appelle moi stp and LATAM)"
                                    multiple={true}
                                />
                            </Grid>
                        )}

                        {applicableFilters.includes('wbs') && (
                            <Grid item xs={12}>
                                <MasterDataAutoComplete
                                    context={$}
                                    logger={logger}
                                    getOptions={getOptions}
                                    path={'WbsPrnSet'}
                                    filter={(value) => `IdNo eq '05' and (PrnNo eq '${value}')`}
                                    value={wbs ?? []}
                                    getOptionLabel={(option) => (option ? `${option.PrnNo} - ${option.PrnName}` : '')}
                                    isOptionEqualToValue={(option, value) => option.PrnNo === value.PrnName}
                                    onChange={(value) => setWbs(value)}
                                    textFieldLabel="WBS Elements"
                                    multiple={true}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>
            );

            return getFilterDrawer(
                open,
                formInvalid,
                'Please select at least one filter',
                toggleDrawer,
                getFilterList,
                onApply,
                data
            );
        };
    }
}

registerBrick('018c1b1d201d3f431a1f', MassChangeFilterDrawer);
